import React from 'react';

/**
 * Central Nervous System developmental pyramid.
 * 8 rows, 22 sections. Teacher rates each 1-5; blank = "Not yet assessed".
 * Color scale is a smooth red -> yellow -> green gradient (HSL 0 -> 120).
 */

export const CNS_PYRAMID_ROWS = [
  [{ key: 'academic_learning', label: 'Academic Learning' }],
  [
    { key: 'daily_living_activities', label: 'Daily Living Activities' },
    { key: 'behaviour', label: 'Behaviour' },
  ],
  [
    { key: 'auditory_language_skill', label: 'Auditory Language Skill' },
    { key: 'visual_spatial_perception', label: 'Visual Spatial Perception' },
    { key: 'attention_centre_functions', label: 'Attention Centre Functions' },
  ],
  [
    { key: 'eye_hand_coordination', label: 'Eye Hand Coordination' },
    { key: 'ocular_motor_control', label: 'Ocular Motor Control' },
    { key: 'postural_adjustment', label: 'Postural Adjustment' },
  ],
  [
    { key: 'body_scheme', label: 'Body Scheme' },
    { key: 'reflex_maturity', label: 'Reflex Maturity' },
    { key: 'ability_to_screen_input', label: 'Ability to Screen Input' },
  ],
  [
    { key: 'postural_security', label: 'Postural Security' },
    { key: 'awareness_of_two_sides_of_body', label: 'Awareness of Two Sides of Body' },
    { key: 'motor_planning', label: 'Motor Planning' },
  ],
  [
    { key: 'olfactory', label: 'Olfactory' },
    { key: 'visual', label: 'Visual' },
    { key: 'auditory', label: 'Auditory' },
    { key: 'gustatory', label: 'Gustatory' },
  ],
  [
    { key: 'tactile', label: 'Tactile' },
    { key: 'vestibular', label: 'Vestibular' },
    { key: 'proprioception', label: 'Proprioception' },
  ],
];

export const CNS_ALL_SECTIONS = CNS_PYRAMID_ROWS.flat();

const APEX_X = 400;
const APEX_Y = 40;
const BASE_Y = 700;
const BASE_HALF = 340;
const H = BASE_Y - APEX_Y;
const ROW_H = H / CNS_PYRAMID_ROWS.length;

const halfW = (y) => ((y - APEX_Y) / H) * BASE_HALF;
const leftX = (y) => APEX_X - halfW(y);
const rightX = (y) => APEX_X + halfW(y);

// Smooth red (0) -> yellow (60) -> green (120) gradient
export function cnsRatingColor(rating) {
  if (!rating) return '#F8FAFC';
  const hue = ((rating - 1) / 4) * 120;
  return `hsl(${hue}, 80%, 72%)`;
}
export function cnsRatingBorder(rating) {
  if (!rating) return '#CBD5E1';
  const hue = ((rating - 1) / 4) * 120;
  return `hsl(${hue}, 55%, 40%)`;
}

const CnsPyramid = ({ ratings = {}, onChange, editable = false, showLegend = true }) => {
  const updateRating = (key, value) => {
    if (!editable || !onChange) return;
    const next = { ...(ratings || {}) };
    if (value === 0 || value === null || value === '') {
      delete next[key];
    } else {
      next[key] = value;
    }
    onChange(next);
  };

  const hasAny = ratings && Object.keys(ratings).length > 0;

  return (
    <div className="space-y-4" data-testid="cns-pyramid">
      <svg viewBox="0 0 800 760" className="w-full max-w-2xl mx-auto block" role="img" aria-label="Central Nervous System developmental pyramid">
        {/* Outer pyramid outline */}
        <polygon
          points={`${APEX_X},${APEX_Y} ${leftX(BASE_Y)},${BASE_Y} ${rightX(BASE_Y)},${BASE_Y}`}
          fill="#FFFFFF"
          stroke="#14B8A6"
          strokeWidth="3"
        />

        {CNS_PYRAMID_ROWS.map((row, rowIdx) => {
          const topY = APEX_Y + rowIdx * ROW_H;
          const bottomY = topY + ROW_H;
          const tL = leftX(topY);
          const tR = rightX(topY);
          const bL = leftX(bottomY);
          const bR = rightX(bottomY);
          const N = row.length;
          const tSpan = (tR - tL) / N;
          const bSpan = (bR - bL) / N;

          return row.map((cell, cellIdx) => {
            const tLX = tL + cellIdx * tSpan;
            const tRX = tLX + tSpan;
            const bLX = bL + cellIdx * bSpan;
            const bRX = bLX + bSpan;
            const rating = ratings?.[cell.key];
            const cellMinX = Math.min(tLX, bLX);
            const cellMaxX = Math.max(tRX, bRX);
            const cellWidth = cellMaxX - cellMinX;

            return (
              <g key={cell.key} data-testid={`cns-cell-${cell.key}`}>
                <polygon
                  points={`${tLX},${topY} ${tRX},${topY} ${bRX},${bottomY} ${bLX},${bottomY}`}
                  fill={cnsRatingColor(rating)}
                  stroke={cnsRatingBorder(rating)}
                  strokeWidth="1.5"
                />
                <foreignObject
                  x={cellMinX}
                  y={topY}
                  width={cellWidth}
                  height={ROW_H}
                >
                  <div
                    xmlns="http://www.w3.org/1999/xhtml"
                    style={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '2px 4px',
                      textAlign: 'center',
                      color: '#0F172A',
                      fontFamily: 'Inter, system-ui, sans-serif',
                      lineHeight: 1.15,
                      userSelect: 'none',
                    }}
                  >
                    <div style={{ fontSize: rowIdx === 0 ? '11px' : '11px', fontWeight: 600 }}>
                      {cell.label}
                    </div>
                    {rating ? (
                      <div style={{ fontSize: '13px', fontWeight: 800, marginTop: '1px' }}>
                        {rating}/5
                      </div>
                    ) : null}
                  </div>
                </foreignObject>
              </g>
            );
          });
        })}

        {/* Base band */}
        <rect x="20" y="710" width="760" height="40" fill="#14B8A6" rx="4" />
        <text
          x={APEX_X}
          y="736"
          textAnchor="middle"
          fill="#FFFFFF"
          fontFamily="Inter, system-ui, sans-serif"
          fontWeight="700"
          fontSize="20"
          letterSpacing="1"
        >
          CENTRAL NERVOUS SYSTEM
        </text>
      </svg>

      {!hasAny && !editable && (
        <p className="text-center text-sm text-slate-500 italic" data-testid="cns-not-assessed">
          Not yet assessed
        </p>
      )}

      {showLegend && (
        <div className="flex items-center justify-center gap-3 text-xs text-slate-600" data-testid="cns-legend">
          <span>1</span>
          <div
            className="h-3 w-40 rounded-full"
            style={{ background: 'linear-gradient(90deg, hsl(0,80%,72%) 0%, hsl(60,80%,72%) 50%, hsl(120,80%,72%) 100%)' }}
          />
          <span>5</span>
        </div>
      )}

      {editable && (
        <div className="border border-slate-200 rounded-lg p-4 space-y-3" data-testid="cns-editor">
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-600">Rate each section (optional)</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 gap-y-2">
            {CNS_ALL_SECTIONS.map((cell) => {
              const rating = ratings?.[cell.key] || 0;
              return (
                <div key={cell.key} className="flex items-center justify-between gap-2">
                  <span className="text-xs text-slate-700 flex-1">{cell.label}</span>
                  <div className="flex items-center gap-1" data-testid={`cns-rate-${cell.key}`}>
                    {[1, 2, 3, 4, 5].map((v) => (
                      <button
                        key={v}
                        type="button"
                        onClick={() => updateRating(cell.key, rating === v ? 0 : v)}
                        className="w-6 h-6 rounded text-[10px] font-bold border transition-colors"
                        style={{
                          background: rating === v ? cnsRatingColor(v) : '#FFFFFF',
                          borderColor: rating === v ? cnsRatingBorder(v) : '#CBD5E1',
                          color: rating === v ? '#0F172A' : '#64748B',
                        }}
                        data-testid={`cns-rate-${cell.key}-${v}`}
                        aria-label={`Rate ${cell.label} ${v} out of 5`}
                      >
                        {v}
                      </button>
                    ))}
                    <button
                      type="button"
                      onClick={() => updateRating(cell.key, 0)}
                      className="ml-1 text-[10px] text-slate-400 hover:text-slate-700 underline"
                      title="Clear"
                      data-testid={`cns-rate-${cell.key}-clear`}
                    >
                      clear
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

export default CnsPyramid;
